/**
 * Load globe points from a Google Sheet published as CSV
 * (File → Share → Publish to web → the data sheet → Comma-separated values).
 *
 * Columns are matched by header name (case, spaces and underscores ignored),
 * so they can be reordered and extra columns are ignored:
 *   Year · Title · Legend · Listening_Modes · Author · Description
 *   City · Country · Latitude · Longitude · Ocean_Basin · Link · Media
 *
 * Mirrors scripts/xlsx_to_globe_points.py — keep LEGEND_TYPES in sync.
 */

const LEGEND_TYPES = {
    'music piece': 'Musical Piece',
    'music': 'Musical Piece',
    'album': 'Musical Piece',
    'composition': 'Musical Piece',
    'whale recording': 'Musical Piece',
    'publication': 'Publication',
    'publicationpublication': 'Publication',
    'book': 'Publication',
    'documentary': 'Publication',
    'whalish discovery': 'Whale discovery',
    'project/archive': 'Project / archive',
    'archive/project': 'Project / archive',
    'project': 'Project / archive',
    'legislation': 'Legislation',
    'event': 'Event',
    'ceremony': 'Ceremony',
    'celemony': 'Ceremony',
    'person': 'Person',
    'art exhibition': 'Art',
    'art piece': 'Art',
    'art installation': 'Art',
    'place': 'Place'
}

/** RFC 4180 CSV → array of rows (arrays of strings). */
function parseCsv(text)
{
    const rows = []
    let row = []
    let field = ''
    let quoted = false
    for (let i = 0; i < text.length; i++)
    {
        const ch = text[i]
        if (quoted)
        {
            if (ch === '"' && text[i + 1] === '"') { field += '"'; i++ }
            else if (ch === '"') quoted = false
            else field += ch
        }
        else if (ch === '"') quoted = true
        else if (ch === ',') { row.push(field); field = '' }
        else if (ch === '\n' || ch === '\r')
        {
            if (ch === '\r' && text[i + 1] === '\n') i++
            row.push(field); rows.push(row)
            row = []; field = ''
        }
        else field += ch
    }
    if (field !== '' || row.length) { row.push(field); rows.push(row) }
    return rows
}

const headerKey = (h) => h.toLowerCase().replace(/[\s_]+/g, '')

function coord(raw, limit)
{
    const v = parseFloat(String(raw).trim())
    return Number.isFinite(v) && Math.abs(v) <= limit ? v : null
}

function legendType(raw)
{
    const key = raw.trim().toLowerCase().split(';')[0].trim()
    return LEGEND_TYPES[key] ?? 'Other'
}

const httpOnly = (v) => (v.startsWith('http') ? v : '')

function rowsToPoints(rows)
{
    const [header, ...body] = rows
    const col = Object.fromEntries(header.map((h, i) => [headerKey(h), i]))
    if (col.title == null || col.latitude == null || col.longitude == null)
        throw new Error('Sheet is missing a Title, Latitude or Longitude column')

    const out = []
    for (const cells of body)
    {
        const get = (name) => (col[name] == null ? '' : (cells[col[name]] ?? '').trim())
        const title = get('title')
        if (!title) continue

        let lat = coord(get('latitude'), 90)
        let lng = coord(get('longitude'), 180)
        if (lat == null || lng == null || Math.abs(lat) + Math.abs(lng) < 1e-9) lat = lng = null

        const record = {
            id: `glb_sheet_${String(out.length + 1).padStart(3, '0')}`,
            date: get('year'),
            title,
            text: get('description'),
            media: httpOnly(get('media')),
            link: httpOnly(get('link')),
            type: legendType(get('legend')),
            latitude: lat,
            longitude: lng,
            location: get('city'),
            country: get('country'),
            keyPeople: get('author')
        }
        if (get('oceanbasin')) record.oceanBasin = get('oceanbasin')
        if (get('listeningmodes')) record.listeningModes = get('listeningmodes')
        out.push(record)
    }
    return out
}

/**
 * Fetch and convert the sheet. Resolves to `fallback` (with a console warning)
 * when no URL is set, the request fails or times out, or the sheet has no rows.
 */
export async function loadSheetPoints(url, fallback, timeoutMs = 8000)
{
    if (!url) return fallback
    try
    {
        const res = await fetch(url, { signal: AbortSignal.timeout(timeoutMs) })
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        const points = rowsToPoints(parseCsv((await res.text()).replace(/\r\n?/g, '\n')))
        if (points.length === 0) throw new Error('Sheet has no rows with a title')
        return points
    }
    catch (err)
    {
        console.warn('Could not load the live data sheet; using bundled data instead.', err)
        return fallback
    }
}
